import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CommandPalette } from './CommandPalette.tsx';
import { Dashboard } from './Dashboard.tsx';
import { Login } from './Login.tsx';
import { ManualReservationPage } from './ManualReservationPage.tsx';
import { OnayMasasi } from './OnayMasasi.tsx';
import { ReservationDetail } from './ReservationDetail.tsx';
import { ReservationsList } from './ReservationsList.tsx';
import { SalonGirisi } from './SalonGirisi.tsx';
import { Sidebar } from './Sidebar.tsx';
import { StudentsList } from './StudentsList.tsx';
import { Topbar } from './Topbar.tsx';
import { WhatsappStatus } from './WhatsappStatus.tsx';
import { clearAdminSession, fetchAdminMe, getAdminSession } from './auth.ts';
import { ToastProvider } from './ui/Toast.tsx';

export function AdminShell() {
  return (
    <ToastProvider>
      <MotionConfig reducedMotion="user">
        <Routes>
          <Route path="login" element={<Login />} />
          <Route element={<AdminLayout />}>
            <Route index element={<OnayMasasi />} />
            <Route path="salon-girisi" element={<SalonGirisi />} />
            <Route path="ozet" element={<Dashboard />} />
            <Route path="rezervasyonlar" element={<ReservationsList />} />
            <Route path="rezervasyonlar/yeni" element={<ManualReservationPage />} />
            <Route path="rezervasyonlar/:id" element={<ReservationDetail />} />
            <Route path="ogrenciler" element={<StudentsList />} />
            <Route path="whatsapp" element={<WhatsappStatus />} />
          </Route>
        </Routes>
      </MotionConfig>
    </ToastProvider>
  );
}

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [adminName, setAdminName] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: auth check runs once per mount
  useEffect(() => {
    const s = getAdminSession();
    const fallbackNext = `${location.pathname}${location.search}`;
    if (!s) {
      navigate(`/admin/login?next=${encodeURIComponent(fallbackNext)}`, { replace: true });
      return;
    }
    fetchAdminMe()
      .then((me) => setAdminName(me.name))
      .catch(() => {
        clearAdminSession();
        navigate(`/admin/login?next=${encodeURIComponent(fallbackNext)}`, { replace: true });
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ⌘K / Ctrl+K opens the command palette anywhere in the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close the mobile drawer on route change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: react to path changes
  useEffect(() => setMenuOpen(false), [location.pathname]);

  if (checking) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-sm text-text-faint">
        Oturum kontrol ediliyor…
      </div>
    );
  }

  const logout = () => {
    clearAdminSession();
    navigate('/admin/login', { replace: true });
  };
  const name = adminName ?? '—';

  return (
    <div className="min-h-dvh bg-bg lg:grid lg:grid-cols-[260px_1fr]">
      {/* Static sidebar on large screens */}
      <div className="hidden lg:block">
        <Sidebar adminName={name} onLogout={logout} />
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <div className="fixed inset-0 z-[800] lg:hidden">
            <motion.button
              type="button"
              aria-label="Menüyü kapat"
              onClick={() => setMenuOpen(false)}
              className="modal-backdrop absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 36 }}
              className="absolute inset-y-0 left-0 w-[260px]"
            >
              <Sidebar adminName={name} onLogout={logout} onNavigate={() => setMenuOpen(false)} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="flex min-h-dvh flex-col">
        <Topbar onOpenMenu={() => setMenuOpen(true)} onOpenPalette={() => setPaletteOpen(true)} />
        <main className="flex-1 px-4 py-6 md:px-8 md:py-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
